import { BUSINESS_TIMEZONE } from '@omecare/utils';
import { useState } from 'react';
import useMoveAgendaSlotMutation from '@/hooks/mutations/use-move-agenda-slot';
import { useGetMoveSlotOptionsQuery } from '@/hooks/queries/use-get-move-slot-options';
import { popModal } from '..';

export type MoveAgendaSlotModalApi = {
  sourceSlotId: string;
  reservationId?: string;
  onSuccess?: () => void;
  onClose?: () => void;
  onCancel?: () => void;
};

export const useMoveAgendaSlotModalViewModel = (
  api: MoveAgendaSlotModalApi,
) => {
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

  const {
    data: moveOptions,
    isLoading: isLoadingOptions,
    isError: isOptionsError,
  } = useGetMoveSlotOptionsQuery({
    sourceSlotId: api.sourceSlotId,
    timezone: BUSINESS_TIMEZONE,
    reservationId: api.reservationId,
  });

  const { mutate: moveSlot, isPending: isMoving } = useMoveAgendaSlotMutation();

  const handleClose = () => {
    popModal('MoveAgendaSlotModal');
    api.onClose?.();
  };

  const handleCancel = () => {
    popModal('MoveAgendaSlotModal');
    api.onCancel?.();
  };

  const handleConfirm = () => {
    if (!selectedSlotId) return;

    moveSlot(
      {
        sourceSlotId: api.sourceSlotId,
        targetSlotId: selectedSlotId,
        reservationId: api.reservationId,
      },
      {
        onSuccess: () => {
          popModal('MoveAgendaSlotModal');
          api.onSuccess?.();
        },
      },
    );
  };

  const handleSelectSlot = (slotId: string) => {
    setSelectedSlotId(slotId);
  };

  return {
    data: {
      moveOptions,
      selectedSlotId,
      isLoading: isLoadingOptions,
      isError: isOptionsError,
      isMoving,
      hasAvailableSlots: (moveOptions?.availableSlots.length ?? 0) > 0,
    },
    api: {
      close: handleClose,
      cancel: handleCancel,
      confirm: handleConfirm,
      selectSlot: handleSelectSlot,
    },
  };
};

export type MoveAgendaSlotModalViewModel = ReturnType<
  typeof useMoveAgendaSlotModalViewModel
>;
